import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { ASSET_APP_IDS } from "@zapier/canvas-constants";
import { zapMetadataAsyncAtomFamily } from "../zaps/zapAsyncAtomFamily";
import { AssetType, Service, NodeType } from "../types";
import { fetchTableFieldsAsyncAtomFamily } from "./fetchTableFieldsAsyncAtomFamily";
import { fetchChatbotAsyncAtomFamily } from "./fetchChatbotAsyncAtomFamily";
import { fetchInterfacePageAsyncAtomFamily } from "./fetchInterfacePageAsyncAtomFamily";
import { fetchInterfaceProjectAsyncAtomFamily } from "./fetchInterfaceProjectAsyncAtomFamily";

type AssetParams = {
  assetType: AssetType;
  assetId: string | null;
  interfaceProjectId: string | undefined;
};

export const assetAsyncAtomFamily = atomFamily(
  ({ assetType, assetId, interfaceProjectId }: AssetParams) => {
    const baseAtom = atom(async (get) => {
      if (assetType === "zap" && assetId) {
        return await get(zapMetadataAsyncAtomFamily(assetId));
      }

      if (assetType === "table" && assetId) {
        return await get(fetchTableFieldsAsyncAtomFamily(assetId));
      }

      if (assetType === "chatbot" && assetId) {
        return await get(fetchChatbotAsyncAtomFamily(assetId));
      }

      // assetId for interfaces will be the pageId
      if (assetType === "interface") {
        if (assetId) {
          return await get(fetchInterfacePageAsyncAtomFamily(assetId));
        }

        if (interfaceProjectId) {
          return await get(
            fetchInterfaceProjectAsyncAtomFamily(interfaceProjectId),
          );
        }
      }

      return null;
    });

    baseAtom.debugLabel = `assetAtomFamily(${JSON.stringify({ assetType, assetId, interfaceProjectId })})`;

    return baseAtom;
  },
  (a, b) =>
    a.assetType === b.assetType &&
    a.assetId === b.assetId &&
    a.interfaceProjectId === b.interfaceProjectId,
);

export function determineAssetType(
  selectedApps: Service[] | undefined,
  nodeType: NodeType,
): AssetType {
  if (nodeType === "zap") {
    return "zap";
  }

  const appId = selectedApps?.[0]?.currentImplementation.id ?? "";
  const isTablesNode = appId.includes(ASSET_APP_IDS.TABLES);
  const isInterfacesNode = appId.includes(ASSET_APP_IDS.INTERFACES);
  const isChatbotsNode = appId.includes(ASSET_APP_IDS.CHATBOTS);

  if (isTablesNode) {
    return "table";
  }

  if (isInterfacesNode) {
    return "interface";
  }

  if (isChatbotsNode) {
    return "chatbot";
  }

  return undefined;
}
